import { useState, useEffect, useRef } from 'react';
import { Sparkles, Send, Loader2, X, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { aiStream } from '../lib/ai';

/**
 * Floating AI chat panel used by both the Concierge (customer-facing) and
 * the Admin Ops Copilot. Streams responses via SSE from the given endpoint.
 */
export default function AIChatPanel({
  open,
  onClose,
  endpoint,           // '/ai/concierge' or '/ai/admin/copilot'
  title,
  subtitle,
  colorClass = 'from-orange-500 to-orange-600',
  starterPrompts = [],
  testIdPrefix = 'ai-chat',
}) {
  const [messages, setMessages] = useState([]); // { role, text }
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: trimmed }, { role: 'assistant', text: '' }]);
    setStreaming(true);
    try {
      await aiStream(endpoint, { message: trimmed, session_id: sessionId }, {
        onSession: (sid) => setSessionId(sid),
        onDelta: (chunk) => {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last?.role === 'assistant') {
              copy[copy.length - 1] = { ...last, text: last.text + chunk };
            }
            return copy;
          });
        },
        onError: (err) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: 'assistant', text: `⚠️ ${err || 'AI error'}`, error: true };
            return copy;
          });
        },
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[min(420px,calc(100vw-2rem))] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div className={`bg-gradient-to-r ${colorClass} p-4 text-white flex items-start justify-between`}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-base">{title}</div>
            {subtitle && <div className="text-xs opacity-90">{subtitle}</div>}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
          data-testid={`${testIdPrefix}-close`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <Bot className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
            <div className="text-sm text-zinc-500 mb-4">Powered by Gemini · Ask anything</div>
            {starterPrompts.length > 0 && (
              <div className="space-y-2">
                {starterPrompts.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="block w-full text-left text-sm px-3 py-2 bg-white border border-zinc-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors"
                    data-testid={`${testIdPrefix}-starter`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-orange-500 text-white rounded-br-sm'
                  : m.error
                  ? 'bg-red-50 border border-red-200 text-red-800 rounded-bl-sm'
                  : 'bg-white border border-zinc-200 text-zinc-900 rounded-bl-sm'
              }`}
            >
              {m.text || (streaming && i === messages.length - 1 ? '···' : '')}
            </div>
          </div>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-zinc-200 p-3 bg-white flex gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          disabled={streaming}
          className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-orange-400 disabled:opacity-50"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="submit"
          size="sm"
          disabled={!input.trim() || streaming}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid={`${testIdPrefix}-send`}
        >
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}
